import React, { useState } from "react";
import {
  Button,
  Container,
  Grid,
  Snackbar,
  TextField,
  Typography,
  Box,
  Avatar,
  CircularProgress,
  IconButton,
  Modal,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Link, useNavigate } from "react-router-dom";
import ApiServices from "../../Utils/ApiServices/ApiServices";
import ClinicLogo from "../../Assests/cosmos  Logo.png";
import CryptoJS from 'crypto-js';

const ProviderLogin = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [welcomeMessageOpen, setWelcomeMessageOpen] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        user_role: "users",
        username: username,
        password: password,
        client_id: process.env.REACT_APP_CLINTID_ACS_TKN,
        scope: process.env.REACT_APP_PROVIDER_SCOPES,
        grant_type: "password",
      };

      const { data, error } = await ApiServices.post(
        "/oauth2/default/token",
        payload,
        {
          "Content-Type": "application/x-www-form-urlencoded",
        }
      );

      if (error) {
        setError(
          error.response?.data?.error_description || "Something went wrong"
        );
      } else {
        // console.log("Login successful:", data);
        const response = await ApiServices.get(
          `/apis/default/api/practitioner/${username}`,
          {
            Authorization: `Bearer ${data.access_token}`,
          }
        );
        const { id, fname, lname, email, user_type,facility,facility_id,city,zip,street } = response.data["0"];
        const capitalizedFname = fname.charAt(0).toUpperCase() + fname.slice(1);
        const capitalizedLname = lname.charAt(0).toUpperCase() + lname.slice(1);
        const welcomeMessage = `Welcome back to work, ${capitalizedFname} ${capitalizedLname}`;
        setWelcomeMessage(welcomeMessage);
        showWelcomeMessage();

        const userData = { id, fname, lname, email, user_type,facility,facility_id,city,zip,street };
        const secretKey = process.env.REACT_APP_USER_ENC_DEC_KEY;
        const encryptedUserData = CryptoJS.AES.encrypt(JSON.stringify(userData), secretKey).toString();
      const encryptedUserType = CryptoJS.AES.encrypt(user_type, secretKey).toString();
      const encryptedAccessToken = CryptoJS.AES.encrypt(data.access_token, secretKey).toString();
      const encryptedRefreshToken = CryptoJS.AES.encrypt(data.refresh_token, secretKey).toString();

      sessionStorage.setItem("LoginUserDetails", encryptedUserData);
      sessionStorage.setItem("LoginUserType", encryptedUserType);
      sessionStorage.setItem("LoginUserAccessToken", encryptedAccessToken);
      sessionStorage.setItem("LoginUserRefreshToken", encryptedRefreshToken);
      }
    } catch (error) {
      console.error("Login error:", error);
      setError("Failed to login,Please Check Your Login Credentials");
    } finally {
      setLoading(false);
    }
  };

  const showWelcomeMessage = () => {
    setWelcomeMessageOpen(true);
    setTimeout(() => {
      setWelcomeMessageOpen(false);
      navigate("/provider/dashboard");
    }, 3000); // Adjust as needed
  };

  const handleSnackbarClose = () => {
    setError(null);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #012A4A 0%, #025E73 35%, #028391 65%, #03A9A3 100%)",
      }}
    >
      <Container
        component="main"
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Grid
          container
          spacing={5}
          sx={{ justifyContent: "center", alignItems: "center" }}
        >
          <Grid
            item
            xs={12}
            sm={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <img
                src={ClinicLogo}
                alt="Doctor"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: "8px",
                }}
              />
            </Box>
            <Typography
              variant="body1"
              align="center"
              sx={{ mb: 4, color: "#fff" }}
            >
              Login to manage patient records efficiently and securely.
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                bgcolor: "background.default",
                borderRadius: "8px",
                boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
                p: 4,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Avatar
                  sx={{ bgcolor: "#05B6B3", mb: 2, width: 80, height: 80 }}
                >
                  <AccountCircleIcon sx={{ width: 60, height: 60 }} />
                </Avatar>
              </Box>
              <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
                Doctor Login
              </Typography>
              <form onSubmit={handleLogin} style={{ width: "100%" }}>
                <TextField
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
                <TextField
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ mt: 3 }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Sign In"
                  )}
                </Button>
                <Grid container justifyContent="space-between" sx={{ mt: 3 }}>
                  <Grid item>
                    {/* <Link href="#" variant="body2" style={{ color: "#05B6B3" }}>
                      Forgot password?
                    </Link> */}
                  </Grid>
                  <Grid item>
                    <Link to="/patient-signin" variant="body2" style={{ color: "#05B6B3" }}>
                      Patient Login
                    </Link>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </Grid>
        </Grid>
        <Snackbar
          open={error !== null}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            onClose={handleSnackbarClose}
            severity="error"
          >
            {error}
          </MuiAlert>
        </Snackbar>
        <Modal
          open={welcomeMessageOpen}
          onClose={() => setWelcomeMessageOpen(false)}
          aria-labelledby="welcome-back-modal"
          aria-describedby="welcome-back-message"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: "8px",
            }}
          >
            <Typography variant="h6" component="h2" align="center" gutterBottom>
              Welcome Message
            </Typography>
            <Typography variant="body1" align="center">
              {welcomeMessage}
            </Typography>
          </Box>
        </Modal>
      </Container>
    </Box>
  );
};

export default ProviderLogin;
